/**
 * Контейнер вокруг ``HeatmapCanvas`` — переключатель слоёв и грид по
 * страницам. Используется и в карточке отправки, и в карточке документа.
 *
 * Страницы 1..``pagesCount`` показываются всегда — даже если по странице
 * нет ни кликов, ни внимания. Так пользователь видит «пустоту» там, где
 * никто не смотрел.
 *
 * UX:
 *   - сверху раздела — легенда + краткое объяснение, что значит цвет;
 *   - под каждой миниатюрой — компактные метрики (клики, внимание);
 *   - можно отсортировать страницы «сначала активные»;
 *   - клик по миниатюре открывает фокус-модалку с большим канвасом и
 *     списком блоков внимания.
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type {
  HeatmapLayer,
  HeatmapPage,
  HeatmapResponse,
} from "../api/analytics";
import { HeatmapCanvas } from "./HeatmapCanvas";
import { formatMs, pluralRu } from "../lib/format";
import { cn } from "../lib/utils";

interface Props {
  heatmap: HeatmapResponse;
  pagesCount: number;
  /** PRD §7.5: текущее состояние фильтра «уникальные сессии». */
  uniqueSessions: boolean;
  /** Callback родителю, чтобы тот перезапросил heatmap с новым фильтром. */
  onUniqueSessionsChange: (next: boolean) => void;
}

const LAYER_LABELS: Record<HeatmapLayer, string> = {
  clicks: "Клики",
  attention: "Внимание",
  both: "Оба слоя",
};

interface PageStats {
  clicks: number;
  totalAttentionMs: number;
  maxSamples: number;
  hasData: boolean;
}

function computeStats(page: HeatmapPage | undefined): PageStats {
  if (page === undefined) {
    return { clicks: 0, totalAttentionMs: 0, maxSamples: 0, hasData: false };
  }
  const clicks = page.clicks.length;
  let totalMs = 0;
  let maxSamples = 0;
  for (const b of page.attention) {
    totalMs += b.total_attention_ms;
    if (b.sample_count > maxSamples) maxSamples = b.sample_count;
  }
  return {
    clicks,
    totalAttentionMs: totalMs,
    maxSamples,
    hasData: clicks > 0 || totalMs > 0,
  };
}

export function HeatmapPagesView({
  heatmap,
  pagesCount,
  uniqueSessions,
  onUniqueSessionsChange,
}: Props) {
  const [layer, setLayer] = useState<HeatmapLayer>(heatmap.layer);
  const [sortByActivity, setSortByActivity] = useState(false);
  const [focusPage, setFocusPage] = useState<number | null>(null);

  const byPage = useMemo(() => {
    const map = new Map<number, HeatmapPage>();
    for (const p of heatmap.pages) map.set(p.page, p);
    return map;
  }, [heatmap.pages]);

  const attentionMax = useMemo(() => {
    let max = 0;
    for (const p of heatmap.pages) {
      for (const b of p.attention) {
        if (b.total_attention_ms > max) max = b.total_attention_ms;
      }
    }
    return max;
  }, [heatmap.pages]);

  const ordered = useMemo(() => {
    const items = Array.from({ length: pagesCount }, (_, idx) => {
      const pageNumber = idx + 1;
      const page = byPage.get(pageNumber);
      return { pageNumber, page, stats: computeStats(page) };
    });
    if (!sortByActivity) return items;
    return [...items].sort((a, b) => {
      if (a.stats.hasData !== b.stats.hasData) return a.stats.hasData ? -1 : 1;
      const aScore = a.stats.totalAttentionMs + a.stats.clicks * 1000;
      const bScore = b.stats.totalAttentionMs + b.stats.clicks * 1000;
      if (bScore !== aScore) return bScore - aScore;
      return a.pageNumber - b.pageNumber;
    });
  }, [byPage, pagesCount, sortByActivity]);

  const totals = useMemo(() => {
    let clicks = 0;
    let totalMs = 0;
    let activePages = 0;
    for (const item of ordered) {
      clicks += item.stats.clicks;
      totalMs += item.stats.totalAttentionMs;
      if (item.stats.hasData) activePages += 1;
    }
    return { clicks, totalMs, activePages };
  }, [ordered]);

  const focusItem = useMemo(() => {
    if (focusPage === null) return null;
    return ordered.find((i) => i.pageNumber === focusPage) ?? null;
  }, [focusPage, ordered]);

  if (pagesCount <= 0) {
    return (
      <div className="text-sm text-muted-foreground">Документ без страниц.</div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Где получатели <span className="font-medium text-red-600">кликали</span>{" "}
        и сколько{" "}
        <span className="font-medium text-orange-600">смотрели</span> каждый блок
        страницы. Насыщенность цвета — пропорциональна интенсивности.
        Кликните по странице, чтобы рассмотреть подробнее.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <div
          className="flex items-center gap-1.5"
          role="group"
          aria-label="Слой тепловой карты"
        >
          {(["clicks", "attention", "both"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setLayer(value)}
              aria-pressed={layer === value}
              className={cn(
                "px-3 py-1.5 rounded-md border text-sm",
                layer === value
                  ? "bg-foreground text-background border-foreground"
                  : "bg-background hover:bg-accent border-input",
              )}
            >
              {LAYER_LABELS[value]}
            </button>
          ))}
        </div>

        <div className="h-6 w-px bg-border" aria-hidden="true" />

        <div
          className="flex items-center gap-1.5"
          role="group"
          aria-label="Фильтр уникальных сессий"
        >
          {(
            [
              { value: false, label: "Все события" },
              { value: true, label: "Уникальные сессии" },
            ] as const
          ).map((option) => (
            <button
              key={String(option.value)}
              type="button"
              onClick={() => onUniqueSessionsChange(option.value)}
              aria-pressed={uniqueSessions === option.value}
              className={cn(
                "px-3 py-1.5 rounded-md border text-sm",
                uniqueSessions === option.value
                  ? "bg-foreground text-background border-foreground"
                  : "bg-background hover:bg-accent border-input",
              )}
            >
              {option.label}
            </button>
          ))}
          <HelpHint label="Что это значит?">
            <p>
              <span className="font-medium">Все события</span> — считается
              каждый клик и каждая секунда внимания.
            </p>
            <p>
              <span className="font-medium">Уникальные сессии</span> — N
              кликов одного получателя в одной сессии считаются как один;
              внимание — как «сколько сессий смотрело блок».
            </p>
          </HelpHint>
        </div>

        <div className="h-6 w-px bg-border" aria-hidden="true" />

        <label className="inline-flex items-center gap-2 text-sm select-none">
          <input
            type="checkbox"
            checked={sortByActivity}
            onChange={(e) => setSortByActivity(e.target.checked)}
            className="h-4 w-4 rounded border-input"
            data-testid="heatmap-sort-active"
          />
          Сначала активные
        </label>
      </div>

      <Legend attentionMax={attentionMax} totals={totals} layer={layer} />

      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
        {ordered.map(({ pageNumber, page, stats }) => (
          <PageThumb
            key={pageNumber}
            pageNumber={pageNumber}
            page={page}
            stats={stats}
            layer={layer}
            attentionMax={attentionMax}
            onActivate={() => setFocusPage(pageNumber)}
          />
        ))}
      </div>

      {focusItem !== null && (
        <FocusModal
          pageNumber={focusItem.pageNumber}
          page={focusItem.page}
          stats={focusItem.stats}
          layer={layer}
          attentionMax={attentionMax}
          totalPages={pagesCount}
          onClose={() => setFocusPage(null)}
          onNavigate={(next) => setFocusPage(next)}
        />
      )}
    </div>
  );
}

interface HelpHintProps {
  label: string;
  children: ReactNode;
}

/**
 * Кнопка «?» со всплывающей справкой. Нативный ``title`` не годится:
 * он появляется только при наведении с задержкой, а на клик и тач не
 * реагирует — пользователи жмут на иконку и ничего не видят.
 *
 * - наведение мышью — показывает справку, пока курсор над «?» или поповером;
 * - клик / тап — закрепляет справку, повторный клик — закрывает;
 * - клик снаружи и Esc — закрывают.
 *
 * Hover слушаем только для ``pointerType === "mouse"``: на тач-устройствах
 * тап тоже шлёт pointerenter, и без фильтра он открыл бы справку, а
 * следующий за ним click тут же бы её закрыл.
 */
function HelpHint({ label, children }: HelpHintProps) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hovered || pinned;
  const rootRef = useRef<HTMLSpanElement>(null);
  const popoverId = useId();

  function close() {
    setPinned(false);
    setHovered(false);
  }

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) close();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <span
      ref={rootRef}
      className="relative inline-flex"
      onPointerEnter={(e) => {
        if (e.pointerType === "mouse") setHovered(true);
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === "mouse") setHovered(false);
      }}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => (pinned ? close() : setPinned(true))}
        className={cn(
          "inline-flex h-6 w-6 cursor-help items-center justify-center rounded-full border text-xs text-muted-foreground hover:bg-accent",
          open && "bg-accent text-foreground",
        )}
      >
        ?
      </button>
      {open && (
        // pt-2 вместо mt-2: прозрачный отступ входит в зону hover, иначе
        // курсор «проваливается» в щель между «?» и поповером и тот гаснет.
        <div className="absolute left-1/2 top-full z-20 w-72 max-w-[calc(100vw-2rem)] -translate-x-1/2 pt-2">
          <div
            id={popoverId}
            role="tooltip"
            data-testid="heatmap-help-popover"
            className="space-y-1.5 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg"
          >
            {children}
          </div>
        </div>
      )}
    </span>
  );
}

interface LegendProps {
  attentionMax: number;
  layer: HeatmapLayer;
  totals: { clicks: number; totalMs: number; activePages: number };
}

function Legend({ attentionMax, layer, totals }: LegendProps) {
  const showClicks = layer === "clicks" || layer === "both";
  const showAttention = layer === "attention" || layer === "both";

  return (
    <div
      data-testid="heatmap-legend"
      className="flex flex-wrap items-center gap-4 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
    >
      {showClicks && (
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">Клики</span>
          <span
            className="inline-block h-3 w-20 rounded-sm border"
            style={{
              background:
                "linear-gradient(to right, rgba(220,38,38,0), rgba(220,38,38,0.55))",
            }}
            aria-hidden="true"
          />
          <span>0 → пик</span>
        </div>
      )}
      {showAttention && (
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">Внимание</span>
          <span
            className="inline-block h-3 w-20 rounded-sm border"
            style={{
              background:
                "linear-gradient(to right, rgba(234,88,12,0.1), rgba(234,88,12,0.65))",
            }}
            aria-hidden="true"
          />
          <span>0 → {formatMs(attentionMax)} (общий масштаб документа)</span>
        </div>
      )}
      <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>
          Активных страниц:{" "}
          <span className="font-medium text-foreground">
            {totals.activePages}
          </span>
        </span>
        <span>·</span>
        <span>
          Всего:{" "}
          <span className="font-medium text-foreground">
            {totals.clicks} клик{pluralRu(totals.clicks, "", "а", "ов")}
          </span>{" "}
          /{" "}
          <span className="font-medium text-foreground">
            {formatMs(totals.totalMs)}
          </span>{" "}
          внимания
        </span>
      </div>
    </div>
  );
}

interface PageThumbProps {
  pageNumber: number;
  page: HeatmapPage | undefined;
  stats: PageStats;
  layer: HeatmapLayer;
  attentionMax: number;
  onActivate: () => void;
}

function PageThumb({
  pageNumber,
  page,
  stats,
  layer,
  attentionMax,
  onActivate,
}: PageThumbProps) {
  return (
    <div
      className="space-y-2"
      data-testid={`heatmap-thumb-${pageNumber}`}
      data-has-data={stats.hasData ? "true" : "false"}
    >
      <HeatmapCanvas
        layer={layer}
        pageNumber={pageNumber}
        clicks={page?.clicks ?? []}
        attention={page?.attention ?? []}
        attentionMax={attentionMax}
        onActivate={onActivate}
      />
      <div className="text-xs">
        {stats.hasData ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="font-medium">Стр. {pageNumber}</span>
            <span className="text-muted-foreground">·</span>
            <span title={`${stats.clicks} кликов всего`}>
              <span className="text-red-600">●</span> {stats.clicks}
            </span>
            <span title={`${formatMs(stats.totalAttentionMs)} внимания`}>
              <span className="text-orange-600">▮</span>{" "}
              {formatMs(stats.totalAttentionMs)}
            </span>
            {stats.maxSamples > 0 && (
              <span
                className="text-muted-foreground"
                title="макс. число замеров на блок"
              >
                {stats.maxSamples} сесс.
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between text-muted-foreground">
            <span>Стр. {pageNumber}</span>
            <span>нет данных</span>
          </div>
        )}
      </div>
    </div>
  );
}

interface FocusModalProps {
  pageNumber: number;
  page: HeatmapPage | undefined;
  stats: PageStats;
  layer: HeatmapLayer;
  attentionMax: number;
  totalPages: number;
  onClose: () => void;
  onNavigate: (page: number) => void;
}

function FocusModal({
  pageNumber,
  page,
  stats,
  layer,
  attentionMax,
  totalPages,
  onClose,
  onNavigate,
}: FocusModalProps) {
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && pageNumber > 1)
        onNavigate(pageNumber - 1);
      else if (e.key === "ArrowRight" && pageNumber < totalPages)
        onNavigate(pageNumber + 1);
    }
    document.addEventListener("keydown", handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [pageNumber, totalPages, onClose, onNavigate]);

  const topBlocks = useMemo(() => {
    if (page === undefined) return [];
    return [...page.attention]
      .filter((b) => b.total_attention_ms > 0)
      .sort((a, b) => b.total_attention_ms - a.total_attention_ms)
      .slice(0, 5);
  }, [page]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="heatmap-focus-title"
      data-testid="heatmap-focus"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-5xl rounded-xl border bg-card text-card-foreground shadow-lg max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 id="heatmap-focus-title" className="text-base font-medium">
            Тепловая карта · стр. {pageNumber} из {totalPages}
          </h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onNavigate(pageNumber - 1)}
              disabled={pageNumber <= 1}
              aria-label="Предыдущая страница"
              className="rounded-md border px-2 py-1 text-sm disabled:opacity-40"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => onNavigate(pageNumber + 1)}
              disabled={pageNumber >= totalPages}
              aria-label="Следующая страница"
              className="rounded-md border px-2 py-1 text-sm disabled:opacity-40"
            >
              →
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрыть"
              data-testid="heatmap-focus-close"
              className="ml-2 rounded-md border px-2 py-1 text-sm"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-[auto_1fr]">
          <div className="flex items-start justify-center">
            <HeatmapCanvas
              layer={layer}
              pageNumber={pageNumber}
              clicks={page?.clicks ?? []}
              attention={page?.attention ?? []}
              attentionMax={attentionMax}
              width={520}
              showTooltip
            />
          </div>
          <div className="space-y-4 text-sm">
            <section className="space-y-1">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Итого по странице
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span>
                  <span className="text-red-600">●</span>{" "}
                  <span className="font-medium">{stats.clicks}</span> клик
                  {pluralRu(stats.clicks, "", "а", "ов")}
                </span>
                <span>
                  <span className="text-orange-600">▮</span>{" "}
                  <span className="font-medium">
                    {formatMs(stats.totalAttentionMs)}
                  </span>{" "}
                  внимания
                </span>
                <span>
                  макс. сессий на блок:{" "}
                  <span className="font-medium">{stats.maxSamples}</span>
                </span>
              </div>
            </section>

            <section className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Топ-5 блоков по вниманию
              </div>
              {topBlocks.length === 0 ? (
                <div className="text-muted-foreground">Нет данных по блокам.</div>
              ) : (
                <ul
                  className="space-y-1"
                  data-testid="heatmap-focus-top-blocks"
                >
                  {topBlocks.map((b) => {
                    const pct = Math.round((b.block_index / 20) * 100);
                    const share =
                      attentionMax > 0
                        ? Math.round((b.total_attention_ms / attentionMax) * 100)
                        : 0;
                    return (
                      <li
                        key={b.block_index}
                        className="flex items-center gap-3"
                      >
                        <span className="w-20 shrink-0 text-muted-foreground">
                          {pct}–{pct + 5}%
                        </span>
                        <span
                          className="h-2 w-32 rounded-sm bg-orange-100"
                          aria-hidden="true"
                        >
                          <span
                            className="block h-full rounded-sm bg-orange-500"
                            style={{ width: `${Math.max(2, share)}%` }}
                          />
                        </span>
                        <span className="font-medium">
                          {formatMs(b.total_attention_ms)}
                        </span>
                        <span className="text-muted-foreground">
                          · {b.sample_count} сесс.
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="text-xs text-muted-foreground">
              Подсказка: наведите курсор на тепловую карту, чтобы увидеть
              детали под точкой. Стрелки ← → переключают страницы, Esc закрывает.
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
